import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DealTask } from '@/hooks/useBrandDeals';
import { format, differenceInDays, parseISO, isValid } from 'date-fns';

type ChartErrorBoundaryState = { hasError: boolean };

class ChartErrorBoundary extends React.Component<React.PropsWithChildren, ChartErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: any) {
    console.error('GanttChart rendering error:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          Unable to render chart
        </div>
      );
    }
    return this.props.children as React.ReactNode;
  }
}

interface GanttChartProps {
  tasks: DealTask[];
}

export const GanttChart: React.FC<GanttChartProps> = ({ tasks }) => {
  if (!tasks || tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No tasks to display in timeline
      </div>
    );
  }

  // Filter tasks that have valid due dates
  const tasksWithValidDates = tasks.filter(task => {
    if (!task.due_date) return false;
    const parsedDate = parseISO(task.due_date);
    return isValid(parsedDate);
  });
  
  if (tasksWithValidDates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No tasks with valid due dates to display
      </div>
    );
  }

  // Find the earliest and latest dates
  const dates = tasksWithValidDates.map(task => parseISO(task.due_date));
  const validDates = dates.filter(date => isValid(date));
  
  if (validDates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No valid dates found for timeline
      </div>
    );
  }

  const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));

  // Prepare data for the chart
  const chartData = tasksWithValidDates.map((task, index) => {
    const dueDate = parseISO(task.due_date);
    
    // Use actual start_date if available, otherwise assume 7-day duration
    let startDate: Date;
    let duration: number;
    
    if (task.start_date) {
      startDate = parseISO(task.start_date);
      duration = Math.max(1, differenceInDays(dueDate, startDate));
    } else {
      // Fallback: assume 7-day duration
      startDate = new Date(dueDate);
      startDate.setDate(startDate.getDate() - 7);
      duration = 7;
    }
    
    const startDays = Math.max(0, differenceInDays(startDate, minDate));
    
    return {
      name: task.title,
      start: Number.isFinite(startDays) ? startDays : 0,
      duration: Number.isFinite(duration) ? duration : 7,
      progress: task.progress_percentage,
      status: task.status,
      start_date: task.start_date ? format(parseISO(task.start_date), 'MMM dd') : null,
      due_date: format(dueDate, 'MMM dd'),
      y: index,
    };
  });

  // Ensure chart data has only finite numbers
  const safeChartData = chartData.filter(d => Number.isFinite(d.start) && Number.isFinite(d.duration));

  // Calculate totalDays from safeChartData to ensure numeric stability
  const totalDays = Math.max(
    1, // Minimum of 1 day
    ...safeChartData.map(d => {
      const total = (Number(d.start) || 0) + (Number(d.duration) || 0);
      return Number.isFinite(total) ? total : 1;
    })
  );

  // Generate safe ticks (max ~8 ticks)
  const maxTicks = 8;
  const step = Math.max(1, Math.ceil(totalDays / maxTicks));
  const ticks = Array.from({ length: Math.floor(totalDays / step) + 1 }, (_, i) => i * step);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border rounded-lg p-4 shadow-xl">
          <p className="font-semibold text-base mb-2">{data.name}</p>
          <div className="space-y-1 text-sm">
            {data.start_date && (
              <p className="text-muted-foreground">Start: <span className="font-medium text-foreground">{data.start_date}</span></p>
            )}
            <p className="text-muted-foreground">Due: <span className="font-medium text-foreground">{data.due_date}</span></p>
            <p className="text-muted-foreground">Progress: <span className="font-medium text-foreground">{data.progress}%</span></p>
            <p className="text-muted-foreground">Status: <span className="font-medium text-foreground capitalize">{data.status}</span></p>
          </div>
        </div>
      );
    }
    return null;
  };

  const getBarColor = (status: string, progress: number) => {
    if (status === 'completed') return '#10b981'; // green
    if (progress > 50) return '#3b82f6'; // blue
    if (progress > 0) return '#f59e0b'; // amber
    return '#6b7280'; // gray
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border p-4">
        <div className="h-96">
          <ChartErrorBoundary>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={safeChartData}
                layout="vertical"
                margin={{ top: 20, right: 40, left: 120, bottom: 5 }}
                barCategoryGap="25%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis 
                  type="number"
                  allowDecimals={false}
                  domain={[0, totalDays]}
                  ticks={ticks}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickFormatter={(value) => {
                    const n = typeof value === 'number' ? value : Number(value);
                    if (!Number.isFinite(n)) return '';
                    try {
                      const date = new Date(minDate);
                      date.setDate(date.getDate() + n);
                      return format(date, 'MMM dd');
                    } catch {
                      return '';
                    }
                  }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={110}
                  tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--accent))' }} />
                <Bar 
                  dataKey="start" 
                  stackId="a" 
                  fill="transparent" 
                  isAnimationActive={false} 
                  maxBarSize={24} 
                />
                <Bar 
                  dataKey="duration" 
                  stackId="a" 
                  fill="hsl(var(--primary))" 
                  maxBarSize={24}
                  radius={[4, 4, 4, 4]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartErrorBoundary>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-4 px-2">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#6b7280' }}></div>
          <span className="text-muted-foreground">Not Started</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#f59e0b' }}></div>
          <span className="text-muted-foreground">In Progress</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#3b82f6' }}></div>
          <span className="text-muted-foreground">Near Completion</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }}></div>
          <span className="text-muted-foreground">Completed</span>
        </div>
      </div>
    </div>
  );
};